import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  CoinsIcon, 
  TrendingUpIcon, 
  PiggyBankIcon, 
  CalculatorIcon,
  SparklesIcon,
  TrophyIcon,
  GemIcon,
  CarIcon,
  ArrowBigUpIcon
} from "lucide-react";
import { CoinFlipGame } from "./CoinFlipGame";
import { SavingsSprintGame } from "./SavingsSprintGame";
import { SavingsStackGame } from "./SavingsStackGame";
import { BudgetRunnerGame } from "./BudgetRunnerGame";
import { SavingsTossGame } from "./SavingsTossGame";

interface Minigame {
  id: string;
  title: string | React.ReactNode;
  description: string;
  icon: React.ReactNode;
  difficulty: "Easy" | "Medium" | "Hard";
  estimatedTime: string;
  comingSoon?: boolean;
}

export const MinigamesView = () => {
  const [selectedGame, setSelectedGame] = useState<Minigame | null>(null);

  const minigames: Minigame[] = [
    {
      id: "coin-flip",
      title: "Coin Flip Challenge",
      description: "Test your luck and learn about probability in investing",
      icon: <CoinsIcon className="w-8 h-8" />,
      difficulty: "Easy",
      estimatedTime: "5 mins"
    },
    {
      id: "savings-sprint",
      title: "Savings Sprint",
      description: "Race to save money while avoiding spending temptations!",
      icon: <ArrowBigUpIcon className="w-8 h-8" />,
      difficulty: "Easy",
      estimatedTime: "7 mins"
    },
    {
      id: "savings-stack",
      title: (
        <div className="flex items-center gap-2">
          <GemIcon className="w-6 h-6 text-accent animate-bounce-small" />
          <span className="bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent font-extrabold">
            Savings Stack
          </span>
          <TrophyIcon className="w-6 h-6 text-accent animate-float" />
        </div>
      ),
      description: "Stack blocks strategically to build your savings tower",
      icon: <PiggyBankIcon className="w-8 h-8" />,
      difficulty: "Medium",
      estimatedTime: "7 mins"
    },
    {
      id: "budget-runner",
      title: "Budget Runner",
      description: "Run towards your financial goals while avoiding debt!",
      icon: <CarIcon className="w-8 h-8" />,
      difficulty: "Easy",
      estimatedTime: "7 mins"
    },
    {
      id: "market-predictor",
      title: "Market Predictor",
      description: "Predict market trends and learn about technical analysis",
      icon: <TrendingUpIcon className="w-8 h-8" />,
      difficulty: "Medium",
      estimatedTime: "10 mins",
      comingSoon: true
    },
    {
      id: "math-master",
      title: "Math Master",
      description: "Quick financial calculations challenge",
      icon: <CalculatorIcon className="w-8 h-8" />,
      difficulty: "Hard",
      estimatedTime: "15 mins",
      comingSoon: true
    },
    {
      id: "savings-toss",
      title: (
        <div className="flex items-center gap-2">
          <PiggyBankIcon className="w-6 h-6 text-pink-500 animate-bounce-small" />
          <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent font-extrabold">
            Savings Toss
          </span>
          <CoinsIcon className="w-6 h-6 text-yellow-500 animate-float" />
        </div>
      ),
      description: "Test your aim and save money in this fun flicking game!",
      icon: <PiggyBankIcon className="w-8 h-8" />,
      difficulty: "Easy",
      estimatedTime: "5 mins"
    }
  ];

  const handleGameSelect = (game: Minigame) => {
    if (!game.comingSoon) {
      setSelectedGame(game);
    }
  };

  const renderGameContent = (game: Minigame) => {
    switch (game.id) {
      case "coin-flip":
        return <CoinFlipGame />;
      case "savings-sprint":
        return <SavingsSprintGame />;
      case "savings-stack":
        return <SavingsStackGame />;
      case "budget-runner":
        return <BudgetRunnerGame />;
      case "savings-toss":
        return <SavingsTossGame />;
      default:
        return (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-4">
                {game.icon}
                {game.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>Game content coming soon...</p>
            </CardContent>
          </Card>
        );
    }
  };

  const getDifficultyColor = (difficulty: "Easy" | "Medium" | "Hard") => {
    switch (difficulty) {
      case "Easy":
        return "text-green-500";
      case "Medium":
        return "text-yellow-500";
      case "Hard":
        return "text-red-500";
      default:
        return "";
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-8 h-8 text-yellow-400 animate-pulse" />
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Game Center
          </h2>
        </div>
        {selectedGame && (
          <Button 
            variant="ghost" 
            onClick={() => setSelectedGame(null)}
            className="hover:bg-secondary/20 group"
          >
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Games
          </Button>
        )}
      </div>

      {!selectedGame ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {minigames.map((game) => (
            <Card 
              key={game.id}
              className={`
                relative overflow-hidden
                group cursor-pointer transition-all duration-300 
                hover:shadow-xl hover:scale-[1.02] 
                ${game.comingSoon ? 'opacity-85' : ''}
                before:absolute before:inset-0 before:bg-gradient-to-b before:from-transparent before:to-black/5
                before:opacity-0 before:transition-opacity before:duration-300
                group-hover:before:opacity-100
              `}
              onClick={() => handleGameSelect(game)}
            >
              <div className="absolute top-2 right-2 z-10">
                {game.comingSoon && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-secondary/20 text-secondary animate-pulse">
                    Coming Soon
                  </span>
                )}
              </div>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-secondary/10 to-primary/10 group-hover:from-secondary/20 group-hover:to-primary/20 transition-colors">
                    <div className="text-primary group-hover:scale-110 transition-transform duration-300">
                      {game.icon}
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">
                      {game.title}
                      {game.comingSoon && (
                        <span className="ml-2 text-sm text-muted-foreground">(Coming Soon)</span>
                      )}
                    </CardTitle>
                    <CardDescription>{game.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Additional content can be added here later */}
              </CardContent>
              <CardFooter className="flex justify-between items-center text-sm mt-auto">
                <span className={`flex items-center gap-2 font-medium ${getDifficultyColor(game.difficulty)}`}>
                  {game.difficulty}
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <span className="w-1 h-1 rounded-full bg-current" />
                  {game.estimatedTime}
                </span>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="animate-fade-in">
          {renderGameContent(selectedGame)}
        </div>
      )}
    </div>
  );
};